import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageBody } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import ResonanceLabel from "../components/ResonanceLabel";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    ArrowsClockwise,
    Sparkle,
    ArrowUpRight,
    Quotes,
    Broadcast,
} from "@phosphor-icons/react";
import CrossPollinationPanel from "../components/CrossPollinationPanel";
import { useAuth } from "../lib/auth";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    } catch {
        return "";
    }
}

const TREND_LABELS = {
    accelerating: { label: "Accelerating", cls: "text-accent" },
    cooling: { label: "Cooling", cls: "text-muted-foreground" },
    steady: { label: "Steady", cls: "text-foreground/70" },
    unknown: { label: "—", cls: "text-muted-foreground" },
};

const RES_LABELS = {
    high: { label: "High", cls: "text-accent" },
    medium: { label: "Medium", cls: "text-foreground" },
    low: { label: "Low", cls: "text-muted-foreground" },
};

const CONF_LABELS = {
    high: { label: "High", cls: "text-foreground" },
    medium: { label: "Medium", cls: "text-foreground/70" },
    low: { label: "Low", cls: "text-muted-foreground" },
};

function TensionCard({ t, index }) {
    const [open, setOpen] = useState(false);
    const trend = TREND_LABELS[t.trend] || TREND_LABELS.unknown;
    const resonance = RES_LABELS[t.resonance] || RES_LABELS.low;
    const confidence = CONF_LABELS[t.confidence] || CONF_LABELS.low;
    const halves = t.halves && t.halves.length >= 2 ? t.halves : null;

    return (
        <article data-testid={`tension-${index}`} className="border-l-2 border-accent pl-5">
            <div className="flex items-baseline gap-3 mb-2">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                    Tension · {String(index + 1).padStart(2, "0")}
                </span>
                <span className={`font-mono text-[10px] tracking-[0.16em] uppercase ${trend.cls}`} data-testid={`tension-trend-${index}`}>
                    · {trend.label}
                </span>
                {t.signal_count > 0 && (
                    <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70 ml-auto">
                        {t.signal_count.toLocaleString()} signals
                    </span>
                )}
            </div>
            {halves ? (
                <p className="editorial text-[19px] md:text-[20px] leading-snug">
                    <span>{halves[0]}</span>
                    <span className="text-accent italic font-normal mx-2">/ but /</span>
                    <span>{halves.slice(1).join(" ")}</span>
                </p>
            ) : (
                <p className="editorial text-[19px] md:text-[20px] leading-snug">{t.text}</p>
            )}

            {/* Editorial restraint: a single quiet line of evidence by default. */}
            {(t.platforms?.length > 0 || t.observed_in_streams?.length > 0) && (
                <p data-testid={`tension-evidence-line-${index}`} className="font-mono text-[11px] text-muted-foreground/85 mt-3 leading-relaxed">
                    {t.platforms?.length > 0 && (
                        <>Observed across <span className="text-foreground/85">{t.platforms.join(" · ")}</span></>
                    )}
                    {t.observed_in_streams?.length > 0 && (
                        <>
                            {t.platforms?.length > 0 && <span className="mx-1.5 text-border">·</span>}
                            Surfacing in <span className="text-foreground/85">{t.observed_in_streams.slice(0, 3).join(", ")}</span>
                            {t.observed_in_streams.length > 3 && <span className="text-muted-foreground/60"> +{t.observed_in_streams.length - 3}</span>}
                        </>
                    )}
                </p>
            )}

            {(t.representative_quote || t.representative_language?.length > 0) && (
                <button
                    type="button"
                    data-testid={`tension-expand-${index}`}
                    onClick={() => setOpen((v) => !v)}
                    className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground transition-colors"
                >
                    {open ? "— close evidence" : "+ open evidence"}
                </button>
            )}

            {open && (
                <div data-testid={`tension-detail-${index}`} className="mt-4 pt-4 border-t border-border/70 space-y-4">
                    {/* Editorial metadata strip — restrained, not analytics */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-[11px] font-mono">
                        <div className="flex items-baseline gap-2">
                            <span className="overline text-[9px] tracking-[0.18em]">resonance</span>
                            <ResonanceLabel
                                label={t.resonance}
                                value={t.resonance_total || 0}
                                breakdown={t.resonance_breakdown}
                                compact
                            />
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="overline text-[9px] tracking-[0.18em]">confidence</span>
                            <span className={confidence.cls}>{confidence.label}</span>
                        </div>
                        {t.recurrence > 1 && (
                            <div className="flex items-baseline gap-2">
                                <span className="overline text-[9px] tracking-[0.18em]">recurrence</span>
                                <span className="tabular-nums">×{t.recurrence}</span>
                            </div>
                        )}
                    </div>

                    {t.representative_language?.length > 0 && (
                        <div>
                            <div className="overline text-[9px] tracking-[0.18em] mb-1.5">Representative language</div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-foreground/85">
                                {t.representative_language.slice(0, 5).map((p, k) => (
                                    <span key={k} className="font-display italic">"{p}"</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {t.representative_quote && (
                        <blockquote className="relative pl-5">
                            <Quotes size={14} className="absolute left-0 top-1 text-accent" />
                            <p className="font-display italic text-foreground/90 text-[15px] leading-snug">
                                "{t.representative_quote}"
                            </p>
                            {t.representative_quote_platform && (
                                <p className="font-mono text-[10px] text-muted-foreground mt-1.5 tracking-wider">
                                    — {t.representative_quote_platform}
                                </p>
                            )}
                        </blockquote>
                    )}
                </div>
            )}
        </article>
    );
}

function FootnoteStrip({ stats }) {
    if (!stats) return null;
    const items = [
        { label: "signals", value: stats.total_comments },
        { label: "sources", value: stats.total_sources },
        { label: "analyses", value: stats.analyses_count },
        { label: "insights", value: stats.insights_count },
        { label: "ideas", value: stats.ideas_total },
        { label: "posted", value: stats.ideas_posted },
    ];
    return (
        <div data-testid="footnote-strip" className="border-t border-border pt-5 mt-12 flex items-center gap-7 flex-wrap">
            {items.map((it) => (
                <div key={it.label} className="flex items-baseline gap-2">
                    <span className="font-mono tabular-nums text-foreground text-sm font-semibold">{it.value ?? 0}</span>
                    <span className="overline text-[10px]">{it.label}</span>
                </div>
            ))}
        </div>
    );
}

export default function Dashboard() {
    const { isAdmin } = useAuth();
    const [stats, setStats] = useState(null);
    const [intel, setIntel] = useState(null);
    const [briefing, setBriefing] = useState(null);
    const [tensionsData, setTensionsData] = useState([]);
    const [synthesizing, setSynthesizing] = useState(false);
    const [sinceDays, setSinceDays] = useState(7);
    const [streams, setStreams] = useState([]);
    const [scopeStreamId, setScopeStreamId] = useState("");  // "" = global / All Streams

    const load = async (days = sinceDays, sid = scopeStreamId) => {
        const params = {};
        if (days > 0) params.since_days = days;
        if (sid) params.stream_id = sid;
        const briefingParams = sid ? { stream_id: sid } : {};
        const tensionParams = { since_days: days || 30, limit: 6 };
        if (sid) tensionParams.stream_id = sid;
        const [s, i, b, t] = await Promise.all([
            api.get("/dashboard/stats", { params }).then((r) => r.data).catch(() => null),
            api.get("/dashboard/intelligence", { params }).then((r) => r.data).catch(() => null),
            api.get("/dashboard/briefing", { params: briefingParams }).then((r) => r.data).catch(() => null),
            api.get("/dashboard/tensions", { params: tensionParams }).then((r) => r.data).catch(() => null),
        ]);
        setStats(s);
        setIntel(i);
        setBriefing(b && b.headline ? b : null);
        setTensionsData(t?.tensions || []);
    };

    useEffect(() => {
        api.get("/streams").then((r) => setStreams(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => { load(sinceDays, scopeStreamId); /* eslint-disable-next-line */ }, [sinceDays, scopeStreamId]);

    const synthesize = async () => {
        setSynthesizing(true);
        try {
            const body = { since_days: sinceDays };
            if (scopeStreamId) body.stream_id = scopeStreamId;
            const r = await api.post("/dashboard/briefing", body);
            setBriefing(r.data);
            toast.success(scopeStreamId ? "Stream briefing synthesized" : "Global briefing synthesized");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not synthesize — run an analysis first.");
        } finally {
            setSynthesizing(false);
        }
    };

    const activeStream = streams.find((s) => s.id === scopeStreamId);
    const scopeLabel = activeStream ? activeStream.name : "All Streams · Global";
    const isStreamScoped = !!scopeStreamId;
    const [ingesting, setIngesting] = useState(false);

    const [pipelineRunning, setPipelineRunning] = useState(false);

    const refreshIntelligence = async () => {
        if (!scopeStreamId) {
            toast.error("Select a specific stream from the scope dropdown first.");
            return;
        }
        setPipelineRunning(true);
        const tid = toast.loading("Refreshing understanding of this narrative territory…");
        try {
            const r = await api.post(`/streams/${scopeStreamId}/pipeline`);
            const { ingest = {}, briefing = {}, signal_count } = r.data || {};
            const inserted = ingest.inserted || 0;
            if (briefing.ok) {
                toast.success(
                    `${inserted} new signal${inserted === 1 ? "" : "s"} · briefing refreshed`,
                    { id: tid, description: briefing.headline ? `"${briefing.headline.slice(0, 100)}…"` : undefined }
                );
            } else if (briefing.error) {
                toast.error(`Ingest OK (${inserted} new) · briefing failed: ${briefing.error.slice(0, 120)}`, { id: tid });
            } else {
                toast(`${inserted} new signal${inserted === 1 ? "" : "s"} · no analyses yet to synthesize`, { id: tid });
            }
            await load(sinceDays, scopeStreamId);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Pipeline failed", { id: tid });
        } finally {
            setPipelineRunning(false);
        }
    };

    const ingestNow = async () => {
        if (!scopeStreamId) {
            toast.error("Select a specific stream from the scope dropdown first.");
            return;
        }
        setIngesting(true);
        try {
            const r = await api.post(`/streams/${scopeStreamId}/ingest`);
            const { inserted = 0, skipped = 0, errors = [] } = r.data || {};
            if (inserted > 0) {
                toast.success(`Ingested ${inserted} new signal${inserted > 1 ? "s" : ""} (${skipped} duplicates)`);
            } else if (errors.length) {
                toast.error(`Ingest hit errors: ${String(errors[0]).slice(0, 90)}`);
            } else {
                toast("No new signals this pass — nothing new on those sources.");
            }
            await load(sinceDays, scopeStreamId);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ingest failed");
        } finally {
            setIngesting(false);
        }
    };

    const noData =
        stats &&
        (stats.analyses_count === 0 || !intel?.latest_analysis) &&
        !briefing;

    // Source data: prefer the AI-synthesized briefing; fall back to latest analysis-derived signals.
    const headline =
        briefing?.headline ||
        intel?.latest_analysis?.title ||
        "The observatory is listening.";

    const lead =
        briefing?.lead ||
        intel?.latest_analysis?.summary ||
        "";

    const tensions =
        briefing?.tensions?.length
            ? briefing.tensions
            : (intel?.top_contradictions || []).map((x) => x.label);

    const verbatim =
        briefing?.verbatim?.length
            ? briefing.verbatim
            : (intel?.top_repeated_phrases || []).map((x) => x.label);

    const quietly =
        briefing?.quietly_true?.length
            ? briefing.quietly_true
            : (intel?.cultural_observations || []).map((x) => ({ observation: x.label, elaboration: "" }));

    const rising =
        briefing?.rising_vocabulary?.length
            ? briefing.rising_vocabulary
            : (intel?.emerging_language || []).map((x) => x.label);

    const felt =
        briefing?.felt_experience?.length
            ? briefing.felt_experience
            : (intel?.top_emotional_themes || []).map((x) => x.label);

    const founderPrompts =
        briefing?.founder_prompts?.length
            ? briefing.founder_prompts
            : (intel?.founder_topics || []).map((x) => x.label);

    return (
        <>
            <header data-testid="dashboard-header" className={`px-8 lg:px-14 pt-12 pb-6 border-b border-border animate-fade-up ${isStreamScoped ? "border-l-[3px] border-l-accent" : ""}`}>
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div>
                        {/* SCOPE BADGE — visually distinct between Global vs Stream-scoped */}
                        <div data-testid="scope-badge" className="mb-3 inline-flex items-center gap-2.5">
                            <span className={`inline-block h-2 w-2 rounded-full ${isStreamScoped ? "bg-accent" : "bg-foreground"}`} />
                            <span className={`overline ${isStreamScoped ? "text-accent" : "text-foreground"}`}>
                                {isStreamScoped ? "Stream Briefing" : "Global Briefing"}
                            </span>
                            <span className="overline text-muted-foreground">·</span>
                            <span data-testid="scope-label" className="font-display font-semibold text-base tracking-tight">
                                {scopeLabel}
                            </span>
                        </div>
                        <div className="overline mb-3">
                            {fmtDate(briefing?.generated_at || intel?.latest_analysis?.created_at || new Date().toISOString())}
                            <span className="mx-2 text-border">·</span>
                            Today's Briefing
                            <span className="mx-2 text-border">·</span>
                            <span className="text-foreground/60">
                                {sinceDays === 0 ? "All-time" : sinceDays === 1 ? "Last 24 hours" : `Last ${sinceDays} days`}
                            </span>
                        </div>
                        <p className="font-mono text-[11px] text-muted-foreground">
                            HAEVN Intelligence · Cross-platform synthesis ·
                            {" "}
                            {Object.keys(intel?.platform_counts || {}).length || 0} platforms ·
                            {" "}
                            {(stats?.total_comments || 0).toLocaleString()} signals ·
                            {" "}
                            {stats?.analyses_count || 0} analyses
                        </p>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                        {/* SCOPE selector — Global or specific stream */}
                        <select
                            data-testid="briefing-scope-selector"
                            value={scopeStreamId}
                            onChange={(e) => setScopeStreamId(e.target.value)}
                            className={`text-[11px] font-mono tracking-wider px-3 py-1.5 rounded-sm border border-border bg-background hover:border-foreground/40 transition-colors focus:outline-none focus:ring-1 focus:ring-foreground/40 ${isStreamScoped ? "border-accent text-accent" : ""}`}
                        >
                            <option value="">All Streams · Global</option>
                            {streams.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                        {/* Time-period selector */}
                        <div data-testid="briefing-period-selector" className="inline-flex border border-border rounded-sm overflow-hidden">
                            {[
                                { v: 1, label: "24h" },
                                { v: 7, label: "7d" },
                                { v: 30, label: "30d" },
                                { v: 0, label: "All" },
                            ].map((p) => (
                                <button
                                    key={p.v}
                                    type="button"
                                    onClick={() => setSinceDays(p.v)}
                                    data-testid={`period-${p.label}`}
                                    className={`px-3 py-1.5 text-[11px] font-mono tracking-wider border-r border-border last:border-r-0 transition-colors ${
                                        sinceDays === p.v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {briefing && (
                            <span data-testid="briefing-model-tag" className="overline text-[10px] self-center text-muted-foreground/80">
                                synthesized · {briefing.model_used?.split(":")[0] || "openai"}
                            </span>
                        )}
                        {isStreamScoped && isAdmin && (
                            <Button
                                data-testid="refresh-intelligence-btn"
                                size="sm"
                                className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
                                onClick={refreshIntelligence}
                                disabled={pipelineRunning}
                                title="Run ingest + analyze + briefing synthesis for this stream in one step"
                            >
                                {pipelineRunning
                                    ? <ArrowsClockwise size={14} className="animate-spin mr-1.5" />
                                    : <Broadcast size={14} weight="duotone" className="mr-1.5" />}
                                {pipelineRunning ? "Refreshing…" : "Refresh intelligence"}
                            </Button>
                        )}
                        {isAdmin && (
                            <Button
                                data-testid="synthesize-briefing-btn"
                                size="sm"
                                variant="outline"
                                className="rounded-sm"
                                onClick={synthesize}
                                disabled={synthesizing || (stats?.analyses_count || 0) === 0}
                                title={(stats?.analyses_count || 0) === 0 ? "Run an analysis first" : ""}
                            >
                                {synthesizing
                                    ? <ArrowsClockwise size={14} className="animate-spin mr-1.5" />
                                    : <Sparkle size={14} weight="duotone" className="mr-1.5" />}
                                {briefing ? "Re-synthesize" : "Synthesize briefing"}
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            <PageBody>
                {noData ? (
                    <EmptyState
                        title={isStreamScoped ? `"${activeStream?.name}" hasn't been heard from yet.` : "The observatory is quiet."}
                        description={
                            isStreamScoped
                                ? "Trigger an ingest on this stream from the Streams page, then run an analysis. The stream-specific briefing will appear here."
                                : "Add a Reddit thread on the Sources page, run Collect Signals, then run an analysis. The briefing will write itself from there."
                        }
                        action={
                            <Link to={isStreamScoped ? `/streams/${scopeStreamId}` : "/sources"}>
                                <Button data-testid="empty-cta-add-source" className="rounded-sm">
                                    {isStreamScoped ? "Open stream" : "Add first source"}
                                </Button>
                            </Link>
                        }
                        testid="dashboard-empty"
                    />
                ) : (
                    <article className="max-w-[1100px] mx-auto">

                        {/* HERO — narrative lead */}
                        <section data-testid="briefing-hero" className="mb-16 grid grid-cols-12 gap-6 lg:gap-10 pt-6">
                            <div className="col-span-12 lg:col-span-9">
                <h1
                                    className="font-display font-bold tracking-[-0.02em] leading-[1.05] text-[clamp(1.85rem,3.4vw,3rem)]"
                                    data-testid="briefing-headline"
                                >
                                    {headline}
                                </h1>
                                {lead && (
                                    <p
                                        data-testid="briefing-lead"
                                        className="editorial-lead drop-cap mt-8 text-[19px] md:text-[21px] text-foreground/90 max-w-[68ch]"
                                    >
                                        {lead}
                                    </p>
                                )}
                            </div>
                            <aside className="col-span-12 lg:col-span-3 lg:border-l lg:border-border lg:pl-6 flex flex-col justify-end">
                                <div className="overline mb-3">Felt experience</div>
                                <ul className="space-y-1.5">
                                    {felt.slice(0, 6).map((t, i) => (
                                        <li key={i} className="font-display font-semibold text-base leading-tight">
                                            <span className="text-muted-foreground/40 font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>
                                            {t}
                                        </li>
                                    ))}
                                    {felt.length === 0 && (
                                        <li className="text-sm text-muted-foreground italic">—</li>
                                    )}
                                </ul>
                            </aside>
                        </section>

                        {/* Cross-platform validation strip — proves narratives appear across multiple platforms */}
                        {Object.keys(intel?.platform_counts || {}).length > 0 && (
                            <section data-testid="briefing-platforms" className="mb-12 pt-2">
                                <div className="flex items-baseline gap-3 mb-3">
                                    <div className="overline">Cross-platform validation</div>
                                    <div className="h-px bg-border flex-1" />
                                    <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider">
                                        frequency · resonance
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-x-8 gap-y-3">
                                    {Object.entries(intel.platform_counts).map(([platform, count]) => {
                                        const resonance = intel?.platform_resonance?.[platform] || 0;
                                        const label = resonance >= 200 ? "high" : resonance >= 50 ? "medium" : "low";
                                        return (
                                            <div key={platform} data-testid={`platform-strip-${platform}`} className="flex flex-col">
                                                <span className="font-display font-semibold text-base">{platform}</span>
                                                <div className="font-mono text-xs tabular-nums text-muted-foreground flex items-center gap-2">
                                                    <span>{count.toLocaleString()} signals</span>
                                                    <span className="text-border">·</span>
                                                    <ResonanceLabel
                                                        label={label}
                                                        value={resonance}
                                                        breakdown={{
                                                            factors: [
                                                                `${count.toLocaleString()} signals from ${platform}`,
                                                                `Aggregate engagement ${resonance.toLocaleString()}`,
                                                            ],
                                                            signal_count: count,
                                                            platform_count: 1,
                                                        }}
                                                        compact
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        {/* TENSIONS — evidence-backed, expandable */}
                        {(tensionsData.length > 0 || tensions.length > 0) && (
                            <section data-testid="briefing-tensions" className="mb-16">
                                <div className="flex items-baseline gap-3 mb-7">
                                    <div className="overline">Tensions</div>
                                    <div className="h-px bg-border flex-1" />
                                    <span className="font-mono text-[11px] text-muted-foreground tracking-wider">
                                        evidence-backed
                                    </span>
                                    <span className="font-mono text-[11px] text-muted-foreground">
                                        {(tensionsData.length || tensions.length).toString().padStart(2, "0")}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                                    {(tensionsData.length > 0
                                        ? tensionsData.slice(0, 6)
                                        : tensions.slice(0, 6).map((s) => ({ text: s, halves: [], signal_count: 0, platforms: [], observed_in_streams: [], trend: "unknown", resonance: "low", confidence: "low", recurrence: 0, representative_language: [], representative_quote: "" }))
                                    ).map((t, i) => (
                                        <TensionCard key={i} t={t} index={i} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* CROSS-POLLINATION — only on Global scope. Surfaces narratives recurring across multiple streams. */}
                        {!isStreamScoped && (
                            <CrossPollinationPanel sinceDays={sinceDays} />
                        )}

                        {/* VERBATIM — pull quotes */}
                        {verbatim.length > 0 && (
                            <section data-testid="briefing-verbatim" className="mb-16">
                                <div className="flex items-baseline gap-3 mb-7">
                                    <div className="overline">Verbatim · what they keep saying</div>
                                    <div className="h-px bg-border flex-1" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {verbatim.slice(0, 6).map((q, i) => (
                                        <blockquote
                                            key={i}
                                            data-testid={`verbatim-${i}`}
                                            className="relative pl-10 pr-4 py-3"
                                        >
                                            <Quotes size={28} weight="fill" className="absolute left-0 top-1 text-primary/15" />
                                            <p className="editorial italic text-[18px] leading-snug text-foreground">
                                                {q.replace(/^["']|["']$/g, "")}
                                            </p>
                                        </blockquote>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* QUIETLY TRUE — observations as essay paragraphs */}
                        {quietly.length > 0 && (
                            <section data-testid="briefing-observations" className="mb-16">
                                <div className="flex items-baseline gap-3 mb-7">
                                    <div className="overline">What's quietly true</div>
                                    <div className="h-px bg-border flex-1" />
                                </div>
                                <div className="space-y-9 max-w-[68ch]">
                                    {quietly.slice(0, 4).map((o, i) => (
                                        <div key={i} data-testid={`observation-${i}`} className="group">
                                            <div className="flex items-baseline gap-3 mb-2">
                                                <span className="font-mono text-[11px] text-primary tabular-nums">№ {String(i + 1).padStart(2, "0")}</span>
                                                <div className="h-px bg-border flex-1 opacity-60" />
                                            </div>
                                            <h3 className="font-display font-bold text-2xl tracking-tight leading-[1.15] mb-2">
                                                {o.observation}
                                            </h3>
                                            {o.elaboration && (
                                                <p className="editorial text-[16px] text-muted-foreground leading-relaxed">
                                                    {o.elaboration}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* RISING VOCABULARY + FOUNDER PROMPTS — two-column footer rail */}
                        <section className="mb-12 grid grid-cols-1 md:grid-cols-2 gap-12 pt-6 border-t border-border">
                            {rising.length > 0 && (
                                <div data-testid="briefing-rising">
                                    <div className="overline mb-4">Rising vocabulary</div>
                                    <ul className="flex flex-wrap gap-x-4 gap-y-2">
                                        {rising.slice(0, 10).map((r, i) => (
                                            <li key={i} className="font-display font-medium text-base leading-tight border-b border-border pb-0.5 hover:border-primary transition-colors">
                                                {r}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {founderPrompts.length > 0 && (
                                <div data-testid="briefing-founder">
                                    <div className="overline mb-4">For the founder camera</div>
                                    <ol className="space-y-3">
                                        {founderPrompts.slice(0, 4).map((p, i) => (
                                            <li key={i} className="flex gap-3 items-start text-[15px] leading-snug">
                                                <span className="font-mono text-[11px] text-muted-foreground tabular-nums pt-1">
                                                    {String(i + 1).padStart(2, "0")}
                                                </span>
                                                <span className="editorial">{p}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                        </section>

                        {/* Quick links */}
                        <nav data-testid="briefing-nav" className="flex flex-wrap gap-2 mt-2 mb-6">
                            <Link to="/analyses" className="text-xs font-mono uppercase tracking-[0.18em] hover:text-primary inline-flex items-center gap-1 border-b border-border pb-0.5">
                                Open analyses <ArrowUpRight size={12} />
                            </Link>
                            <span className="text-border mx-2">·</span>
                            <Link to="/insights" className="text-xs font-mono uppercase tracking-[0.18em] hover:text-primary inline-flex items-center gap-1 border-b border-border pb-0.5">
                                Insights archive <ArrowUpRight size={12} />
                            </Link>
                            <span className="text-border mx-2">·</span>
                            <Link to="/content-ideas" className="text-xs font-mono uppercase tracking-[0.18em] hover:text-primary inline-flex items-center gap-1 border-b border-border pb-0.5">
                                Content ideas <ArrowUpRight size={12} />
                            </Link>
                            <span className="text-border mx-2">·</span>
                            <Link to="/trends" className="text-xs font-mono uppercase tracking-[0.18em] hover:text-primary inline-flex items-center gap-1 border-b border-border pb-0.5">
                                Trends <ArrowUpRight size={12} />
                            </Link>
                        </nav>

                        {/* Footnote strip — counts as understated metadata */}
                        <FootnoteStrip stats={stats} />
                    </article>
                )}
            </PageBody>
        </>
    );
}
